import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useSystemStatus } from '../context/SystemStatusContext';
import { formatUserInfo } from '../utils/userUtils';
import { 
  FileText, 
  Brain, 
  Video,
  Crown,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Bell,
  Mail,
  Settings,
  LogOut as LogOutIcon,
  // Search,
  Users,
  Share2,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import PointsAPI from '../services/pointsApi';
import FeedbackModal from '../components/common/FeedbackModal';
import UserSettingsModal from '../components/common/UserSettingsModal';
import AccountSettingsPage from './AccountSettingsPage';
import UserAvatar from '../components/common/UserAvatar';
import PricingPage from './PricingPage';
import CoopPromotionPage from './CoopPromotionPage';
import TicketList from './TicketList';
import NotificationsPanel from '../components/common/NotificationsPanel';
import { InterviewRecordAPI } from '../services/interviewRecordApi';
import feedbackAPI from '../services/feedbackApi';
import calendarEventApi from '../services/calendarEventApi';
import JobDataCharts from '../components/dashboard/JobDataCharts';


// 使用标准懒加载各功能组件
// const JobSearchPage = lazy(() => import('../components/dashboard/Search'));
const ResumeDiagnosis = lazy(() => import('../components/dashboard/ResumeDiagnosis'));
const InterviewPrep = lazy(() => import('../components/dashboard/InterviewPrep'));
const MockInterview = lazy(() => import('../components/dashboard/MockInterview'));
const MeetingAssistant = lazy(() => import('../components/dashboard/MeetingAssistant'));



// 内容组件
const DashboardHome = ({ user, onNavigate, sidebarCollapsed }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
 
  
  // 事件管理状态
  const [events, setEvents] = useState([]);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
 
  
  // 事件类型转换函数：API类型 -> 前端类型
  const mapEventTypeToFrontend = useCallback((type) => {
    if (!type) return 'other';
    const apiType = type.toUpperCase();
    switch (apiType) {
      case 'INTERVIEW':
        return 'interview';
      case 'MEETING':
        return 'meeting';
      case 'OTHER':
        return 'other';
      default:
        return 'other';
    }
  }, []);

  // 加载事件数据
  const loadEvents = useCallback(async () => {
    try {
      const eventsData = await calendarEventApi.getAllEvents();
      const eventItems = Array.isArray(eventsData) 
        ? eventsData 
        : (eventsData?.items || []);
      
      const formattedEvents = eventItems.map(event => ({
        id: event.id,
        title: event.title,
        type: mapEventTypeToFrontend(event.event_type),
        event_type: event.event_type,
        time: event.time || '',
        location: event.location || '',
        date: new Date(event.date),
        status: event.is_completed ? 'completed' : 'upcoming',
        extra_data: event.extra_data || null
      })).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setEvents(formattedEvents);
    } catch (error) {
      setEvents([]);
    }
  }, [mapEventTypeToFrontend]);

  // 组件挂载时加载事件
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);
  
  // 添加事件
  const addEvent = async (eventData) => {
    try {
      // 构建API请求数据，包含已转换的事件类型
      const apiEventData = {
        title: eventData.title,
        event_type: eventData.event_type, // 使用已转换过的类型
        date: eventData.date.toISOString().split('T')[0],
        time: eventData.time || '',
        location: eventData.location || '',
        extra_data: null
      };
      
      await calendarEventApi.createEvent(apiEventData);
      
      // 重新加载事件列表以获取最新数据
      await loadEvents();
    } catch (error) {
    }
  };

  // 编辑事件
  const editEvent = async (eventId, eventData) => {
    try {
      // 构建API请求数据
      const apiEventData = {
        title: eventData.title,
        event_type: eventData.event_type, // 使用已转换过的类型
        date: eventData.date.toISOString().split('T')[0],
        time: eventData.time || '',
        location: eventData.location || '',
        extra_data: eventData.extra_data || null
      };
      
      await calendarEventApi.updateEvent(eventId, apiEventData);
      
      // 重新加载事件列表
      await loadEvents();
    } catch (error) {
    }
  };

  // 删除事件
  const deleteEvent = async (eventId) => {
    try {
      await calendarEventApi.deleteEvent(eventId);
      
      // 重新加载事件列表
      await loadEvents();
    } catch (error) {
    }
  };
  
  // 切换事件状态
  const toggleEventStatus = async (eventId) => {
    try {
      const event = events.find(e => e.id === eventId);
      const newStatus = event.status !== 'completed';
      
      await calendarEventApi.updateEventStatus(eventId, newStatus);
      
      // 重新加载事件列表
      await loadEvents();
    } catch (error) {
    }
  };
  
  const [dailyQuote] = useState([
    "今天也要加油哦！每一步努力都在为梦想铺路 ✨",
    "相信自己，你比想象中更优秀！💪",
    "新的一天，新的机会，加油！🌟",
    "保持微笑，好运自然来 😊",
    "每一次尝试都是成长的机会 🌱"
  ][Math.floor(Math.random() * 5)]);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const formatUserName = (user) => {
    if (user?.name) return user.name;
    if (user?.phone) return user.phone.slice(-4);
    return '朋友';
  };

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return '夜深了';
    if (hour < 12) return '早上好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // 获取指定日期的事件
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();
    
    // 空白日期
    for (let i = 0; i < firstDay; i++) {
      days.push(<CalendarDay key={`empty-${i}`} />);
    }
    
    // 实际日期
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isToday = today.getDate() === day && 
                     today.getMonth() === currentDate.getMonth() && 
                     today.getFullYear() === currentDate.getFullYear();
      const isSelected = selectedDate.getDate() === day && 
                        selectedDate.getMonth() === currentDate.getMonth() && 
                        selectedDate.getFullYear() === currentDate.getFullYear();
      const dayEvents = getEventsForDate(date);
      const hasEvents = dayEvents.length > 0;
      
      days.push(
        <CalendarDay 
          key={day} 
          isToday={isToday}
          isSelected={isSelected}
          hasEvents={hasEvents}
          dayEvents={dayEvents}
          onClick={() => setSelectedDate(date)}
        >
          {day}
          {hasEvents && (
            <EventTypeTag type={dayEvents[0].type}>
              {dayEvents[0].type === 'interview' ? '面试' : 
               dayEvents[0].type === 'meeting' ? '会议' : '其他'}
            </EventTypeTag>
          )}
        </CalendarDay>
      );
    }
    
    return days;
  };

  return (
      <ModernContentWrapper sidebarCollapsed={sidebarCollapsed}>
        {/* 欢迎区域 */}
        <WelcomeSection>
          <WelcomeText>
            <Greeting>{getTimeGreeting()}，{formatUserName(user)}！</Greeting>
            <QuoteText>{dailyQuote}</QuoteText>
          </WelcomeText>
        </WelcomeSection>

        {/* 主要内容区域 */}
        <MainGrid>
          {/* 求职数据图表区域 */}
          <JobDataCharts onNavigate={onNavigate} />

          {/* 日历组件 */}
          <CalendarSection>
            <CalendarHeader>
              <CalendarTitle>
                {currentDate.getFullYear()}年 {currentDate.getMonth() + 1}月
              </CalendarTitle>
              <CalendarNav>
                <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}>
                  <ChevronLeft size={16} />
                </NavButton>
                <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}>
                  <ChevronRight size={16} />
                </NavButton>
              </CalendarNav>
            </CalendarHeader>
            
            <CalendarGrid>
              <WeekHeader>
                {['日', '一', '二', '三', '四', '五', '六'].map(day => (
                  <WeekDay key={day}>{day}</WeekDay>
                ))}
              </WeekHeader>
              <DaysGrid>
                {renderCalendar()}
              </DaysGrid>
            </CalendarGrid>
            
            {/* 事件列表区域 */}
            <EventsSection>
              <EventsHeader>
                {(() => {
                  // 检查所选日期是否为过去日期
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dateToCheck = new Date(selectedDate);
                  dateToCheck.setHours(0, 0, 0, 0);
                  
                  // 只有当所选日期不是过去日期时才显示添加事件按钮
                  if (dateToCheck >= today) {
                    return (
                      <AddEventButton onClick={() => {
                        // 确保在打开模态框前清除之前的编辑状态
                        setEditingEvent(null);
                        // 确保selectedDate是一个Date对象
                        const newSelectedDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
                        if (!isNaN(newSelectedDate.getTime())) {
                          // 强制更新selectedDate状态，确保传递给模态框的是有效的Date对象
                          setSelectedDate(newSelectedDate);
                        }
                        // 打开添加事件模态框
                        setShowAddEventModal(true);
                      }}>
                        + 添加事件
                      </AddEventButton>
                    );
                  }
                  return null; // 过去日期不显示按钮
                })()}
              </EventsHeader>
              
              <EventsList>
                {getEventsForDate(selectedDate).length > 0 ? (
                  getEventsForDate(selectedDate).map(event => (
                    <EventCard key={event.id}>
                      <EventTime>{event.time}</EventTime>
                      <EventDetails>
                        <EventTitle>{event.title}</EventTitle>
                        <EventMeta>
                          <EventType type={event.type}>
                        {event.type === 'interview' ? '面试' : 
                         event.type === 'meeting' ? '会议' : '其他'}
                      </EventType>
                          <EventLocation>{event.location}</EventLocation>
                        </EventMeta>
                      </EventDetails>
                      <EventActions>
                        <EventStatus 
                          status={(function() {
                            const dateObj = new Date(event.date);
                            const parts = (event.time || '').split(':');
                            const h = Number(parts[0] || 0);
                            const m = Number(parts[1] || 0);
                            dateObj.setHours(h, m, 0, 0);
                            const start = dateObj;
                            const soonThreshold = 10;
                            const ongoingDuration = 120;
                            if (event.status === 'completed') return 'completed';
                            const diffMin = Math.round((start.getTime() - now.getTime()) / 60000);
                            if (!event.time) {
                              return now < start ? 'scheduled' : 'in-progress';
                            }
                            if (diffMin > soonThreshold) return 'scheduled';
                            if (diffMin > 0) return 'upcoming';
                            const elapsed = -diffMin;
                            if (elapsed <= ongoingDuration) return 'in-progress';
                            return 'ended';
                          })()}
                          onClick={() => toggleEventStatus(event.id)}
                          clickable
                        >
                          {(function() {
                            const dateObj = new Date(event.date);
                            const parts = (event.time || '').split(':');
                            const h = Number(parts[0] || 0);
                            const m = Number(parts[1] || 0);
                            dateObj.setHours(h, m, 0, 0);
                            const start = dateObj;
                            const soonThreshold = 10;
                            const ongoingDuration = 120;
                            if (event.status === 'completed') return '已完成';
                            const diffMin = Math.round((start.getTime() - now.getTime()) / 60000);
                            if (!event.time) {
                              return now < start ? '待开始' : '进行中';
                            }
                            if (diffMin > soonThreshold) return '待开始';
                            if (diffMin > 0) return '即将开始';
                            const elapsed = -diffMin;
                            if (elapsed <= ongoingDuration) return '进行中';
                            return '已结束';
                          })()}
                        </EventStatus>
                        {event.status !== 'completed' && (
                          <EventActionButton 
                            onClick={() => {
                              setEditingEvent(event);
                              setShowAddEventModal(true);
                            }}
                            title="编辑"
                          >
                            ✏️
                          </EventActionButton>
                        )}
                        <EventActionButton 
                          onClick={() => deleteEvent(event.id)}
                          title="删除"
                          danger
                        >
                          🗑️
                        </EventActionButton>
                      </EventActions>
                    </EventCard>
                  ))
                ) : (
                  <EmptyEvents>
                    <EmptyText>这一天没有安排</EmptyText>
                    <AddEventHint>点击右上角按钮添加新事件</AddEventHint>
                  </EmptyEvents>
                )}
              </EventsList>
            </EventsSection>
          </CalendarSection>
          
          {/* 添加/编辑事件模态框 */}
          {showAddEventModal && (
            <EventModal
              event={editingEvent}
              selectedDate={selectedDate}
              onSave={(eventData) => {
                if (editingEvent) {
                  editEvent(editingEvent.id, eventData);
                } else {
                  addEvent(eventData);
                }
                setShowAddEventModal(false);
                setEditingEvent(null);
              }}
              onClose={() => {
                setShowAddEventModal(false);
                setEditingEvent(null);
              }}
            />
          )}
        </MainGrid>
      </ModernContentWrapper>
    );

};

// 懒加载组件的加载包装器 - 移到组件外部定义
const LoadingFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #666;
  font-size: 16px;
`;

const ComponentWithSuspense = ({ children }) => (
  <Suspense fallback={<LoadingFallback>⚡ 加载中...</LoadingFallback>}>
    {children}
  </Suspense>
);

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Sidebar = styled.div`
  width: ${props => props.collapsed ? '60px' : '200px'};
  background: white;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 100;
  transition: width 0.3s ease;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 20px;
  right: -12px;
  width: 24px;
  height: 24px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    transform: scale(1.1);
  }
  
  .icon {
    width: 14px;
    height: 14px;
    color: #6c757d;
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.collapsed ? '16px 12px' : '16px 16px'};
  border-bottom: 1px solid #e9ecef;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  h2 {
    font-size: ${props => props.collapsed ? '0' : '20px'};
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    opacity: ${props => props.collapsed ? '0' : '1'};
    transition: all 0.3s ease;
    white-space: nowrap;
    overflow: hidden;
    
    &:hover {
      color: #667eea;
    }
  }
  
  .logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    flex-shrink: 0;
    border-radius: 6px;
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: 12px 0;
`;

const NavSection = styled.div`
  margin-top: ${props => props.isFirstWithoutTitle ? '0px' : '0px'};
  margin-bottom: ${props => props.isFirstWithoutTitle ? '8px' : (props.hasTitle ? '10px' : '0px')};
  padding-bottom: ${props => props.isFirstWithoutTitle ? '0px' : '0px'};
  border-bottom: none;
`;

const SectionTitle = styled.div`
  padding: ${props => props.collapsed ? '6px 12px 2px' : '6px 16px 2px'};
  font-size: 12px;
  font-weight: 700;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: ${props => props.collapsed ? '0' : '1'};
  transition: opacity 0.3s ease;
  display: ${props => props.collapsed ? 'none' : 'block'};
  margin-bottom: 4px;
`;

const NavItem = styled.div`
  padding: ${props => props.collapsed ? '8px 12px' : '8px 16px 8px 24px'};
  display: flex;
  align-items: center;
  gap: ${props => props.collapsed ? '0' : '8px'};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.active ? '#667eea' : '#495057'};
  background: ${props => props.active ? '#f8f9ff' : 'transparent'};
  border-right: ${props => props.active ? '3px solid #667eea' : '3px solid transparent'};
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  position: relative;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background: #f8f9fa;
    color: #667eea;
  }
  
  .icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  
  .label {
    flex: 1;
    font-weight: 500;
    opacity: ${props => props.collapsed ? '0' : '1'};
    transition: opacity 0.3s ease;
    white-space: nowrap;
    overflow: hidden;
    display: ${props => props.collapsed ? 'none' : 'block'};
  }
  
  .badge {
    background: #ffc107;
    color: #000;
    font-size: 9px;
    font-weight: 600;
    padding: 1px 4px;
    border-radius: 6px;
    text-transform: uppercase;
    display: ${props => props.collapsed ? 'none' : 'block'};
    transition: all 0.3s ease;
    min-width: 20px;
    text-align: center;
    line-height: 1.2;
    
    &.hot {
      background: #ef4444;
      color: #fff;
      font-size: 9px;
      font-weight: 600;
      padding: 1px 4px;
      border-radius: 6px;
      min-width: 20px;
      text-align: center;
      line-height: 1.2;
    }
  }

  ${props => props.collapsed && `
    &:hover {
      &::after {
        content: attr(data-tooltip);
        position: absolute;
        left: 60px;
        top: 50%;
        transform: translateY(-50%);
        background: #2c3e50;
        color: white;
        padding: 6px 10px;
        border-radius: 6px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 1000;
        pointer-events: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }
      
      &::before {
        content: '';
        position: absolute;
        left: 55px;
        top: 50%;
        transform: translateY(-50%);
        border: 5px solid transparent;
        border-right-color: #2c3e50;
        z-index: 1000;
        pointer-events: none;
      }
    }
  `}
`;

const SidebarFooter = styled.div`
  padding: ${props => props.collapsed ? '10px 12px' : '14px 16px'};
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
  overflow: visible;
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.collapsed ? '0' : '6px'};
  margin-bottom: ${props => props.collapsed ? '4px' : '6px'};
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  position: relative;
  
  .avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    cursor: pointer;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  .info {
    flex: 1;
    opacity: ${props => props.collapsed ? '0' : '1'};
    transition: opacity 0.3s ease;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    .name {
      font-weight: 500;
      color: #2c3e50;
      font-size: 10px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
    }
    
    .notification {
      color: #2c3e50;
      cursor: pointer;
      transition: color 0.2s ease;
      position: relative;
      
      &:hover {
        color: #667eea;
      }

      .badge {
        position: absolute;
        top: -3px;
        right: -8px;
        min-width: 12px;
        height: 12px;
        padding: 0 2px;
        background: #ef4444;
        border-radius: 6px;
        border: 1.5px solid #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
        font-weight: 600;
        color: #ffffff;
        line-height: 1;
        z-index: 10;
      }
    }
  }
`;

const UserDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: ${props => props.collapsed ? '0' : '0'};
  right: ${props => props.collapsed ? 'auto' : '0'};
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: ${props => props.collapsed ? '180px' : '200px'};
  margin-top: 8px;
  overflow: hidden;
  
  ${props => props.collapsed && `
    /* When the sidebar is collapsed, show the dropdown to the right of the avatar
       and above it so that it is fully visible (the previous vertical placement
       caused the card to be rendered below the viewport). */
    left: 60px;          /* horizontal offset to clear the collapsed sidebar */
    min-width: 200px;    /* keep the same width as expanded state */

    /* Override the default vertical placement */
    top: auto;           /* cancel the previously defined top */
    bottom: 100%;        /* position the menu above the avatar */

    /* Spacing adjustments */
    margin-top: 0;
    margin-bottom: 8px;
  `}
`;



const UserDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #495057;
  font-size: 14px;
  border-bottom: 1px solid #f1f3f4;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
    color: #667eea;
  }

  svg {
    color: #6c757d;
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: #667eea;
  }
`;

const CreditsSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.collapsed ? '3px' : '4px'};
  opacity: ${props => props.collapsed ? '0' : '1'};
  height: ${props => props.collapsed ? '0' : 'auto'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  .credits-text {
    font-size: 13px;
    color: #2c3e50;
    font-weight: 500;
  }
  
  .credits-number {
    font-size: 16px;
    color: #667eea;
    font-weight: 600;
  }
`;

const UpgradeCard = styled.div`
  margin-bottom: ${props => props.collapsed ? '3px' : '6px'};
  opacity: ${props => props.collapsed ? '0' : '1'};
  height: ${props => props.collapsed ? '0' : 'auto'};
  overflow: visible;
  transition: all 0.3s ease;
  display: ${props => props.collapsed ? 'none' : 'block'};
  position: relative;
  
  button {
    width: 100%;
    padding: 10px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    position: relative;
    
    .trial-badge {
      position: absolute;
      top: -6px;
      right: -6px;
      display: inline-block;
      padding: 2px 5px;
      background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
      color: #ffffff;
      font-size: 8px;
      font-weight: 600;
      border-radius: 6px;
      line-height: 1.2;
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
      z-index: 10;
      pointer-events: none;
    }
    
    &:hover {
      background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
      transform: translateY(-1px);
    }
    
    svg {
      color: #fbbf24;
    }
  }
`;

const FeedbackButton = styled.button`
  width: 100%;
  padding: ${props => props.collapsed ? '5px' : '5px'};
  background: transparent;
  border: none;
  color: #2c3e50;
  cursor: pointer;
  display: ${props => props.collapsed ? 'none' : 'flex'};
  align-items: center;
  justify-content: flex-start;
  gap: ${props => props.collapsed ? '0' : '6px'};
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 13px;
  margin-top: 1px;
  
  &:hover {
    color: #667eea;
  }
  
  .label {
    opacity: ${props => props.collapsed ? '0' : '1'};
    transition: opacity 0.3s ease;
  }
`;

const MainContent = styled.div`
  position: relative;
  flex: 1;
  margin-left: ${props => props.sidebarHidden ? '0' : (props.sidebarCollapsed ? '60px' : '200px')};
  padding: ${props => props.nopadding ? '0' : '20px 32px 48px 32px'};
  background: #f5f7fa;
  min-height: 100vh;
  transition: ${props => props.sidebarHidden ? 'none' : 'margin-left 0.3s ease'};
  
  /* 大屏幕优化：调整内边距 */
  @media (min-width: 1920px) {
    padding: ${props => props.nopadding ? '0' : '24px 40px 48px 40px'};
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: ${props => props.nopadding ? '0' : '24px 56px 48px 56px'};
  }
  
  /* 中等屏幕 */
  @media (max-width: 1440px) {
    padding: ${props => props.nopadding ? '0' : '20px 24px 48px 24px'};
  }
  
  /* 小屏幕 */
  @media (max-width: 1024px) {
    padding: ${props => props.nopadding ? '0' : '16px 20px 40px 20px'};
  }
  
  /* 移动端 */
  @media (max-width: 768px) {
    margin-left: ${props => props.sidebarHidden ? '0' : (props.sidebarCollapsed ? '60px' : '200px')};
    padding: ${props => props.nopadding ? '0' : '16px 12px 32px 12px'};
  }
`;

 

// 现代化样式组件
const ModernContentWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  padding: 12px 20px 40px 20px;
  margin: 0 auto;
  transition: all 0.3s ease;
  
  /* 大屏幕优化：充分利用屏幕空间 */
  @media (min-width: 1920px) {
    max-width: 1800px;
    padding: 12px 32px 40px 32px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    max-width: 2200px;
    padding: 12px 48px 40px 48px;
  }
  
  /* 中等屏幕 */
  @media (max-width: 1440px) {
    max-width: 100%;
    padding: 12px 24px 40px 24px;
  }
  
  /* 小屏幕 */
  @media (max-width: 1024px) {
    padding: 12px 16px 40px 16px;
  }
  
  /* 移动端 */
  @media (max-width: 768px) {
    padding: 12px 12px 40px 12px;
  }
`;

const WelcomeSection = styled.div`
  padding: 0 20px;
  margin-bottom: 16px;
  color: #2c3e50;
  height: auto;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    padding: 0 32px;
    margin-bottom: 24px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: 0 48px;
    margin-bottom: 32px;
  }
  
  /* 小屏幕 */
  @media (max-width: 768px) {
    padding: 0 12px;
    margin-bottom: 12px;
  }
`;

 

const WelcomeText = styled.div`
  flex: 1;
`;

const Greeting = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 6px 0;
  color: #2c3e50;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    font-size: 28px;
    margin: 0 0 8px 0;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    font-size: 32px;
    margin: 0 0 10px 0;
  }
  
  /* 小屏幕 */
  @media (max-width: 768px) {
    font-size: 20px;
    margin: 0 0 4px 0;
  }
`;

const QuoteText = styled.p`
  font-size: 16px;
  margin: 0;
  color: #6c757d;
  line-height: 1.4;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    font-size: 18px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    font-size: 20px;
  }
  
  /* 小屏幕 */
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 20px;
  margin-top: 24px;
  
  /* 大屏幕优化：增加间距 */
  @media (min-width: 1920px) {
    gap: 32px;
    margin-top: 32px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    gap: 40px;
    margin-top: 40px;
  }
  
  /* 中等屏幕 */
  @media (max-width: 1440px) {
    gap: 20px;
  }
  
  /* 平板 */
  @media (max-width: 1024px) {
    gap: 16px;
    margin-top: 20px;
  }

  /* 移动端 */
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 16px;
  }
`;

const CalendarSection = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fbfbff 100%);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.06);
  border: 1px solid rgba(2, 6, 23, 0.05);
  overflow: hidden;
  
  /* 大屏幕优化 */
  @media (min-width: 1920px) {
    padding: 24px;
    border-radius: 20px;
  }
  
  /* 超大屏幕优化 */
  @media (min-width: 2560px) {
    padding: 32px;
    border-radius: 24px;
  }
  
  /* 小屏幕 */
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CalendarTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
`;

const CalendarNav = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f8f9fa;
  color: #495057;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: #667eea;
    color: white;
    transform: scale(1.05);
  }
`;

const CalendarGrid = styled.div`
  width: 100%;
`;

const WeekHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-weight: 600;
  color: #6c757d;
  font-size: 12px;
  padding: 4px;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

// 辅助：根据事件类型返回提示点颜色
const getEventDotColor = (events) => {
  const type = events && events[0] && events[0].type;
  switch (type) {
    case 'interview':
      return '#5b7cfa';
    case 'meeting':
      return '#34a853';
    case 'deadline':
      return '#e11d48';
    default:
      return '#9ca3af';
  }
};

const CalendarDay = styled.div`
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  transition: all 0.2s ease;
  position: relative;
  padding: 2px;
  border: 1px solid rgba(2, 6, 23, 0.04);
  background: #fff;
  
  ${props => props.isToday && `
    background: #00C0C0;
    color: white;
    font-weight: 700;
  `}
  
  ${props => props.isSelected && !props.isToday && `
    background: #e3f2fd;
    color: #1976d2;
    font-weight: 600;
  `}
  
  &:hover {
    background: ${props => props.isToday ? '#00A0A0' : '#f7f9fc'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(17, 24, 39, 0.06);
  }

  ${props => props.hasEvents ? `
    &::after {
      content: '';
      position: absolute;
      bottom: 6px;
      right: 6px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${getEventDotColor(props.dayEvents)};
      box-shadow: 0 0 0 2px #ffffff;
    }
  ` : ''}
`;

const EventTypeTag = styled.div`
  font-size: 8px;
  font-weight: 500;
  padding: 1px 3px;
  border-radius: 3px;
  margin-top: 1px;
  background: ${props => {
    switch (props.type) {
      case 'interview': return '#e3f2fd';
      case 'meeting': return '#e8f5e8';
      case 'deadline': return '#ffeaea';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'interview': return '#1976d2';
      case 'meeting': return '#2e7d32';
      case 'deadline': return '#d32f2f';
      default: return '#6c757d';
    }
  }};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

// 事件相关样式组件
const EventsSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
`;

const EventsHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
`;

const EventsList = styled.div`
  margin-bottom: 16px;
`;

const EventCard = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 6px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(2px);
  }
`;

const EventTime = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  min-width: 50px;
  margin-right: 12px;
`;

const EventDetails = styled.div`
  flex: 1;
`;

const EventTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
`;

const EventMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EventType = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  background: ${props => {
    switch (props.type) {
      case 'interview': return '#e3f2fd';
      case 'meeting': return '#e8f5e8';
      case 'other': return '#fff3e0';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'interview': return '#1976d2';
      case 'meeting': return '#2e7d32';
      case 'other': return '#ef6c00';
      default: return '#6c757d';
    }
  }};
`;

const EventLocation = styled.span`
  font-size: 11px;
  color: #6c757d;
`;

const EventStatus = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'upcoming': return '#fff3cd';
      case 'completed': return '#d4edda';
      case 'in-progress': return '#cce5ff';
      case 'scheduled': return '#f3f4f6';
      case 'ended': return '#e5e7eb';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'upcoming': return '#856404';
      case 'completed': return '#155724';
      case 'in-progress': return '#004085';
      case 'scheduled': return '#374151';
      case 'ended': return '#6b7280';
      default: return '#6c757d';
    }
  }};
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  ${props => props.clickable && `
    &:hover {
      opacity: 0.8;
      transform: scale(1.05);
    }
  `}
`;

const EmptyEvents = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
`;

const EmptyText = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
`;

const AddEventHint = styled.div`
  font-size: 12px;
  color: #adb5bd;
`;

 

const AddEventButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #111827;
  }
`;

// 事件操作相关样式
const EventActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const EventActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: ${props => props.danger ? '#fee2e2' : '#f3f4f6'};
  color: ${props => props.danger ? '#dc2626' : '#6b7280'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.danger ? '#fecaca' : '#e5e7eb'};
    transform: scale(1.1);
  }
`;

// 自定义下拉框组件
const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find(option => option.value === value) || options[0]
  );

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange(option.value);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.custom-select')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <CustomSelectContainer className="custom-select">
      <CustomSelectTrigger onClick={toggleDropdown} isOpen={isOpen}>
        <SelectContent>
          <SelectText>{selectedOption.label}</SelectText>
        </SelectContent>
        <SelectArrow isOpen={isOpen}>
          <ChevronDown size={16} />
        </SelectArrow>
      </CustomSelectTrigger>
      
      {isOpen && (
        <CustomSelectDropdown>
          {options.map((option) => (
            <CustomSelectOption
              key={option.value}
              onClick={() => handleSelect(option)}
              isSelected={option.value === value}
            >
              <OptionText>{option.label}</OptionText>
            </CustomSelectOption>
          ))}
        </CustomSelectDropdown>
      )}
    </CustomSelectContainer>
  );
};

// 事件模态框组件
const EventModal = ({ event, selectedDate, onSave, onClose }) => {
  // 检查日期是否为过去日期
  const isPastDate = useMemo(() => {
    // 如果是编辑现有事件，不做检查（允许编辑过去事件的时间、地点等）
    if (event) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(selectedDate);
    dateToCheck.setHours(0, 0, 0, 0);
    
    return dateToCheck < today;
  }, [event, selectedDate]);
  
  // 初始化表单数据
  const [formData, setFormData] = useState(() => {
    // 确保日期值正确设置
    let dateValue;
    if (event?.date) {
      // 编辑现有事件时，使用事件的日期
      dateValue = event.date;
    } else if (selectedDate) {
      // 添加新事件时，直接使用selectedDate作为日期
      // 无论selectedDate是什么类型，都尝试转换为Date对象
      dateValue = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
      // 如果转换失败，使用当前日期作为后备
      if (isNaN(dateValue.getTime())) {
        dateValue = new Date();
      }
    } else {
      // 默认使用当前日期
      dateValue = new Date();
    }
    dateValue = new Date(+new Date(dateValue) + 8 * 3600 * 1000)
    return {
      title: event?.title || '',
      type: event?.type || 'interview',
      time: event?.time || '',
      location: event?.location || '',
      date: dateValue // 直接设置日期值
    };
  });
  
  // 合并的useEffect：确保当添加新事件时，日期始终与selectedDate同步
  useEffect(() => {
    // 只有在添加新事件（非编辑现有事件）时才更新日期
    if (!event && selectedDate) {
      // 确保selectedDate是有效的Date对象
      const newDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
      if (!isNaN(newDate.getTime())) {
        // 使用函数式更新确保我们获取最新的状态
        setFormData(prevFormData => ({
          ...prevFormData,
          date: newDate
        }));
      }
    }
  }, [selectedDate, event]); // 依赖项确保在props变化时正确更新

  // 事件类型转换函数：前端类型 -> API类型
  const mapEventTypeToApi = (type) => {
    const normalizedType = typeof type === 'string' ? type.toLowerCase() : 'other';
    switch (normalizedType) {
      case 'interview':
        return 'INTERVIEW';
      case 'meeting':
        return 'MEETING';
      case 'deadline':
      case 'reminder':
        return 'OTHER';
      default:
        return 'OTHER';
    }
  };

  // 事件类型选项
  const eventTypeOptions = [
    {
      value: 'interview',
      label: '面试'
    },
    {
      value: 'meeting',
      label: '会议'
    },
    {
      value: 'other',
      label: '其他'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // 如果是已完成的事件，阻止提交
    if (event && event.status === 'completed') {
      alert('已完成的事件不可编辑');
      return;
    }
    
    // 验证日期是否有效
    const eventDate = formData.date instanceof Date && !isNaN(formData.date.getTime()) ? formData.date : null;
    if (!eventDate) {
      alert('请选择有效的日期');
      return;
    }
    
    // 如果是新事件且选择了过去的日期，阻止提交
    if (!event) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(eventDate);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        alert('不能在过去的日期添加事件');
        return;
      }
    }
    
    if (!formData.title.trim()) {
      alert('请输入事件标题');
      return;
    }
    
    // 创建包含API兼容事件类型的数据对象
    const eventData = {
      ...formData,
      event_type: mapEventTypeToApi(formData.type), // 添加转换后的API类型字段
      date: formData.date
    };
    
    onSave(eventData);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{event ? '编辑事件' : '添加事件'}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <EventForm onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>事件标题</FormLabel>
              <FormInput
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="例如：腾讯前端面试"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>事件类型</FormLabel>
              <CustomSelect
                value={formData.type}
                onChange={(value) => setFormData({...formData, type: value})}
                options={eventTypeOptions}
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>时间</FormLabel>
              <FormInput
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>地点/备注</FormLabel>
              <FormInput
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="例如：腾讯大厦 或 在线"
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>日期</FormLabel>
              <FormInput
                type="date"
                min={!event ? new Date(+new Date() + 8 * 3600 * 1000).toISOString().split('T')[0] : undefined} // 新事件不能选择过去日期
                value={formData.date instanceof Date && !isNaN(formData.date.getTime()) ? formData.date.toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData({...formData, date: new Date(e.target.value)})}
              />
            </FormGroup>
            
            <FormActions>
              <CancelButton type="button" onClick={onClose}>
                取消
              </CancelButton>
              {event && event.status === 'completed' ? (
                <SaveButton type="button" disabled style={{opacity: 0.6, cursor: 'not-allowed'}}>
                  已完成事件不可编辑
                </SaveButton>
              ) : isPastDate ? (
                <SaveButton type="button" disabled style={{opacity: 0.6, cursor: 'not-allowed'}}>
                  不能在过去日期添加事件
                </SaveButton>
              ) : (
                <SaveButton type="submit">
                  {event ? '保存' : '添加'}
                </SaveButton>
              )}
            </FormActions>
          </EventForm>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// 模态框样式组件
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #6b7280;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const EventForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FormInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

 

// 自定义下拉框样式组件
const CustomSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CustomSelectTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 42px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:hover {
    border-color: #667eea;
  }
`;

const SelectContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const SelectText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const SelectArrow = styled.div`
  color: #9ca3af;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CustomSelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 4px;
  overflow: hidden;
  animation: dropdownSlide 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes dropdownSlide {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CustomSelectOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isSelected ? '#f0f4ff' : 'white'};
  border-left: ${props => props.isSelected ? '3px solid #667eea' : '3px solid transparent'};
  
  &:hover {
    background: #f8f9ff;
    transform: translateX(2px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const OptionText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: #667eea;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
  }
`;

 

 

 

 

 

 

// 统计部分样式
 

 

 

// 将reason中的英文单词转换为中文
function translateReason(reason) {
  if (!reason) return reason;
  // 将常见的英文订阅周期转换为中文
  let translated = reason
    .replace(/订阅month充值/gi, '订阅月充值')
    .replace(/订阅week充值/gi, '订阅周充值')
    .replace(/订阅quarter充值/gi, '订阅季度充值')
    .replace(/month/gi, '月')
    .replace(/week/gi, '周')
    .replace(/quarter/gi, '季度');
  return translated;
}

// 删除字符串末尾的所有标点符号（。、！、？等）
function removePunctuation(text) {
  return text.replace(/[。，、！？；：]+$/g, '');
}

function buildPointsMessage(entry) {
  const change = entry.change;
  const abs = Math.abs(change);
  let reason = entry.reason || '';
  // 先转换中英文混杂的内容
  reason = translateReason(reason);
  
  if (reason === 'recharge') return removePunctuation(`充值成功，获得 ${abs} 积分`);
  if (reason === 'referral_success') return removePunctuation(`邀请成功奖励 +${abs} 积分`);
  if (reason === 'initial_bonus') return removePunctuation(`新用户赠送 +${abs} 积分`);
  if (reason === 'subscription_expired_clear' && change < 0) {
    return '会员已到期，本期积分已清零';
  }
  if (change < 0) {
    if (reason === 'resume_diagnosis') {
      return `简历诊断-功能使用扣减 ${abs} 积分`;
    }
    if (reason === 'interview_prep') {
      return `面试押题-功能使用扣减 ${abs} 积分`;
    }
    if (reason === 'mock_interview') {
      return `模拟面试-按时长扣减 ${abs} 积分`;
    }
    if (reason === 'meeting_assistant') {
      return `会议助手-按时长扣减 ${abs} 积分`;
    }
    return removePunctuation(`功能使用扣减 ${abs} 积分（${reason}）`);
  }
  if (change > 0) {
    // 处理"订阅month充值"等中英文混杂的情况
    if (reason.includes('订阅') && reason.includes('充值')) {
      return removePunctuation(`积分变更 +${abs}（${reason}）`);
    }
    return removePunctuation(`积分变更 +${abs}（${reason}）`);
  }
  return removePunctuation(`积分记录（${reason}）`);
}

const DashboardPage = ({ activeView: propActiveView }) => {
  const { user, logout } = useAuth();
  const { isHealthy, status } = useSystemStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [activeView, setActiveView] = useState(propActiveView || 'dashboard');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showUserSettingsModal, setShowUserSettingsModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
 
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationPosition, setNotificationPosition] = useState({ top: 0, left: 0 });
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const [prefetchedNotifications, setPrefetchedNotifications] = useState([]);
  const userId = (user && (user.id?.toString() || user.phone)) || null;
  const userExternalId = user?.user_id;  // UUID for payment and points
  const [balance, setBalance] = useState(0);
  const [vipInfo, setVipInfo] = useState({ vipLevel: 'L0', isVip: false, subscription: null });
  const [vipInfoLoading, setVipInfoLoading] = useState(true); // 添加加载状态，初始为true
  // 使用 userExternalId (user_id) 作为 lastSeenKey 的一部分，确保不同组件使用相同的 key
  const lastSeenKey = useMemo(() => `notifications_last_seen_${userExternalId || userId || 'guest'}`, [userExternalId, userId]);
  // 跟踪上一次的系统状态，用于检测状态变化
  const prevSystemStatusRef = useRef({ isHealthy: true, status: 'healthy' });
  
  // 当用户变化或组件挂载时，重置通知面板状态，防止灰色蒙布
  useEffect(() => {
    setShowNotifications(false);
    setShowUserDropdown(false);
    setShowFeedbackModal(false);
    setShowUserSettingsModal(false);
    setShowLogoutConfirm(false);
    // 用户变化时重置VIP信息加载状态，避免显示错误的"体验版"标签
    setVipInfoLoading(true);
  }, [user?.id]);

  // 加载积分信息
  const loadBalance = useCallback(async () => {
    try {
      if (!userExternalId) return;
      const hist = await PointsAPI.getHistory(userExternalId);
      setBalance(hist.balance || 0);
    } catch (error) {
    }
  }, [userExternalId]);

  // 加载VIP订阅信息
  const loadVipInfo = useCallback(async () => {
    if (!userExternalId) {
      setVipInfoLoading(false);
      return;
    }
    try {
      setVipInfoLoading(true);
      const PaymentAPI = (await import('../services/paymentApi.js')).default;
      const response = await PaymentAPI.getUserSubscription(userExternalId);
      if (response.success) {
        setVipInfo({
          vipLevel: response.vip_level || 'L0',
          isVip: response.is_vip || false,
          subscription: response.subscription
        });
      }
    } catch (error) {
    } finally {
      setVipInfoLoading(false);
    }
  }, [userExternalId]);

  // 初始加载用户信息
  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  useEffect(() => {
    loadVipInfo();
  }, [loadVipInfo]);

  // 监听系统状态恢复，自动刷新用户信息
  useEffect(() => {
    const prevStatus = prevSystemStatusRef.current;
    // 当系统从unhealthy或degraded变为healthy时，刷新用户信息
    if (prevStatus.status !== 'healthy' && isHealthy && status === 'healthy') {
      loadBalance();
      loadVipInfo();
    }
    // 更新上一次的状态
    prevSystemStatusRef.current = { isHealthy, status };
  }, [isHealthy, status, loadBalance, loadVipInfo]);

  // 监听积分更新事件
  useEffect(() => {
    const handlePointsUpdate = async (event) => {
      
      // 重新从API获取最新积分余额
      if (userExternalId) {
        try {
          const pointsRes = await PointsAPI.getHistory(userExternalId).catch(() => ({ entries: [], balance: 0 }));
          
          // 更新积分余额
          if (pointsRes.balance !== undefined) {
            setBalance(pointsRes.balance);
          }
          
          if (pointsRes?.entries) {
            let running = pointsRes.balance ?? 0;
            const newNotifications = [];
            for (const e of pointsRes.entries) {
              const balanceAfter = running;
              newNotifications.push({ 
                id: `points_${e.id}`, 
                type: 'points', 
                title: buildPointsMessage(e), 
                desc: `当前余额 ${balanceAfter} 积分`,
                time: e.created_at 
              });
              running -= e.change;
            }

            // 读取上次查看通知的时间戳
            let lastSeen = 0;
            try { lastSeen = parseInt(localStorage.getItem(lastSeenKey) || '0', 10) || 0; } catch {}

            // 更新预取通知列表的同时，重新计算未读数量（避免重复累加）
            setPrefetchedNotifications(prev => {
              const updated = [...newNotifications, ...prev.filter(n => n.type !== 'points')];
              updated.sort((a, b) => new Date(b.time) - new Date(a.time));

              const unreadCount = updated.filter(
                n => new Date(n.time).getTime() > lastSeen
              ).length;
              setNewNotificationCount(unreadCount);

              return updated;
            });
          }
        } catch (error) {
        }
      }
      
      // 刷新VIP信息
      if (userExternalId) {
        try {
          const PaymentAPI = (await import('../services/paymentApi.js')).default;
          const response = await PaymentAPI.getUserSubscription(userExternalId);
          if (response.success) {
            setVipInfo({
              vipLevel: response.vip_level || 'L0',
              isVip: response.is_vip || false,
              subscription: response.subscription
            });
          }
        } catch (error) {
        }
      }
    };

    window.addEventListener('pointsUpdated', handlePointsUpdate);
    return () => {
      window.removeEventListener('pointsUpdated', handlePointsUpdate);
    };
  }, [userExternalId, lastSeenKey]);

  // 预计算铃铛红点（未读数量）并预取最近10条，供面板瞬时显示
  useEffect(() => {
    const computeBadge = async () => {
      if (!userExternalId) return;
      try {
        const [pointsRes, subscriptionRes] = await Promise.all([
          PointsAPI.getHistory(userExternalId).catch(() => ({ entries: [], balance: 0 })),
          (async () => {
            try {
              const PaymentAPI = (await import('../services/paymentApi.js')).default;
              return await PaymentAPI.getUserSubscription(userExternalId);
            } catch {
              return null;
            }
          })()
        ]);

        const list = [];
        if (pointsRes?.entries) {
          let running = pointsRes.balance ?? 0;
          for (const e of pointsRes.entries) {
            const balanceAfter = running;
            list.push({ 
              id: `points_${e.id}`, 
              type: 'points', 
              title: buildPointsMessage(e), 
              desc: `当前余额 ${balanceAfter} 积分`,
              time: e.created_at 
            });
            running -= e.change;
          }
        }

        if (subscriptionRes && subscriptionRes.success && subscriptionRes.subscription) {
          const sub = subscriptionRes.subscription;
          const endDate = sub.end_date ? new Date(sub.end_date) : null;
          const daysRemaining = typeof sub.days_remaining === 'number' ? sub.days_remaining : null;
          const isActive = sub.is_active;
          
          // 会员已到期通知（优先级更高）
          if (endDate && daysRemaining !== null && daysRemaining === 0 && !isActive) {
            const id = `vip_expired_${sub.end_date}`;
            list.push({
              id,
              type: 'vip_expired',
              title: '会员已到期',
              desc: '您的会员已过期，积分已清零。续费会员可继续使用完整功能',
              time: endDate.toISOString()
            });
          }
          // 会员即将到期提醒
          else if (endDate && daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 3) {
            const id = `vip_expiring_${sub.end_date}`;
            list.push({
              id,
              type: 'vip',
              title: `会员即将到期（剩余 ${daysRemaining} 天）`,
              desc: '为避免积分被清零，请及时续费当前会员周期',
              time: endDate.toISOString()
            });
          }
        }

        // 去重并排序，保留所有通知（显示时通过高度限制约5条，超出可滚动）
        const seen = new Set();
        const deduped = [];
        for (const n of list) {
          if (seen.has(n.id)) continue;
          seen.add(n.id);
          deduped.push(n);
        }
        deduped.sort((a, b) => new Date(b.time) - new Date(a.time));
        const latest = deduped; // 保留所有通知，不再限制数量
        setPrefetchedNotifications(latest);

        let lastSeen = 0;
        try { lastSeen = parseInt(localStorage.getItem(lastSeenKey) || '0', 10) || 0; } catch {}
        const newCount = latest.filter(n => new Date(n.time).getTime() > lastSeen).length;
        setNewNotificationCount(newCount);
      } catch (_) {}
    };
    computeBadge();
  }, [userId, userExternalId, lastSeenKey]);

  // 根据URL路径自动检测当前视图
  useEffect(() => {
    const path = location.pathname;
    if (path === '/app') {
      setActiveView('dashboard');
    } else if (path === '/app/jobs/search') {
      // setActiveView('job-assistant');
    } else if (path === '/app/resumes/diagnosis') {
      setActiveView('resume-diagnosis');
    } else if (path === '/app/interviews/prep') {
      setActiveView('interview-prep');
    } else if (path === '/app/interviews/mock') {
      setActiveView('mock-interview');
    } else if (path === '/app/meetings' || path === '/app/meetings/session') {
      setActiveView('meeting-assistant');
    } else if (path === '/app/settings') {
      setActiveView('setting');
    } else if (path === '/app/coop-promotion') {
      setActiveView('coop-promotion');
    }
    if (path !== '/app/meetings' && path !== '/app/meetings/session') {
      setSidebarHidden(false);
    }
  }, [location.pathname]);

  // 当props中的activeView变化时，更新本地状态
  useEffect(() => {
    if (propActiveView) {
      setActiveView(propActiveView);
    }
  }, [propActiveView]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      navigate('/');
    }
  };

  // 处理用户下拉菜单
  const handleUserDropdownToggle = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // 处理点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleManageAccount = () => {
    setShowUserDropdown(false);
    navigate('/app/settings');
  };

  const handleUserSettingsClose = () => {
    setShowUserSettingsModal(false);
  };

  const handleUserSettingsSave = async (userData) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedUser = { ...user, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      window.location.reload();
      
    } catch (error) {
      throw error;
    }
  };

  const handleSignOut = () => {
    setShowUserDropdown(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    await handleLogout();
  };

  // 通用的导航前确认检查函数
  const confirmBeforeNavigate = useCallback((onConfirm) => {
    // 如果当前在简历诊断页面，检查是否需要确认
    if (activeView === 'resume-diagnosis') {
      // 检查是否有确认函数（表示有未保存的结果）
      if (window.resumeDiagnosisConfirmLeave) {
        const canLeave = window.resumeDiagnosisConfirmLeave(onConfirm);
        // 如果不能立即离开（需要用户确认），则直接返回 false
        if (!canLeave) {
          return false;
        }
      }
    }
    
    // 如果当前在面试押题页面，检查是否需要确认
    if (activeView === 'interview-prep') {
      // 检查是否有确认函数（表示有未保存的结果）
      if (window.interviewPrepConfirmLeave) {
        const canLeave = window.interviewPrepConfirmLeave(onConfirm);
        // 如果不能立即离开（需要用户确认），则直接返回 false
        if (!canLeave) {
          return false;
        }
      }
    }
    
    // 如果能立即离开，执行确认回调
    onConfirm && onConfirm();
    return true;
  }, [activeView]);

  // 处理意见反馈
  const handleFeedbackClick = () => {
    // 意见反馈只是打开弹框，不离开页面，所以不需要确认
    setShowFeedbackModal(true);
  };

  const handleFeedbackClose = () => {
    setShowFeedbackModal(false);
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      await feedbackAPI.create(userId, feedbackData.score, feedbackData.comment);
      alert('感谢您的评分和反馈！我们会继续努力改进产品。');
    } catch (error) {
      alert('提交失败，请稍后重试');
    }
  };

  // 处理合作推广
  const handleCoopPromotionClick = () => {
    confirmBeforeNavigate(() => {
      setActiveView('coop-promotion');
    navigate('/app/coop-promotion');
    });
  };

  // 处理工单支持
  const handleTicketSupportClick = () => {
    confirmBeforeNavigate(() => {
      setActiveView('ticket-support');
    navigate('/app/ticket-support');
    });
  };

  // 处理立即升级按钮
  // 升级按钮应该可以直接离开，不需要确认
  const handleUpgradeClick = () => {
    setActiveView('pricing');
    navigate('/app/pricing');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    // 触发手动折叠事件
    window.dispatchEvent(new Event('sidebarManualToggle'));
  };

 

  // 供会议助手切换全屏（隐藏侧栏）使用
  const handleSessionModeChange = (inSession) => {
    setSidebarHidden(!!inSession);
  };

  const handleNotificationClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // 获取铃铛元素的位置
    const bellElement = event.currentTarget;
    const rect = bellElement.getBoundingClientRect();
    
    // 计算通知卡片的位置
    // 卡片应该显示在铃铛的右侧，向上偏移更多以避免遮挡下方内容
    let cardTop = rect.top - 80; // 向上偏移80px，让卡片显示在更高位置
    let cardLeft = rect.right + 10; // 向右偏移10px
    
    // 如果侧边栏折叠，调整位置
    if (sidebarCollapsed) {
      // 折叠状态下，铃铛在中间，卡片应该向右显示
      cardLeft = rect.right + 15;
    }
    
    // 确保卡片不会超出屏幕右边界
    const maxLeft = window.innerWidth - 300; // 卡片宽度280px + 边距20px
    const finalLeft = Math.min(cardLeft, maxLeft);
    
    // 确保卡片不会超出屏幕顶部，但允许更低的顶部位置
    const minTop = 10;
    const finalTop = Math.max(cardTop, minTop);
    
    setNotificationPosition({
      top: finalTop,
      left: finalLeft
    });
    
    setShowNotifications(true);
  };

  // 按照独立仪表盘、COPILOT 和 TOOLS 分类组织导航菜单
  const navSections = [
    {
      title: '', // 仪表盘作为独立项，不需要标题
      items: [
        {
          id: 'dashboard',
          label: '仪表盘',
          icon: null, // 仪表盘不使用图标
          path: '/app'
        }
      ]
    },
    {
      title: 'COPILOT',
      items: [
        {
          id: 'mock-interview',
          label: '模拟面试',
          icon: <Video className="icon" />,
          path: '/app/interviews/mock'
        },
        {
          id: 'meeting-assistant',
          label: '会议助手',
          icon: <Users className="icon" />,
          path: '/app/meetings'
        }
      ]
    },
    {
      title: 'TOOLS',
      items: [
        // {
        //   id: 'job-assistant',
        //   label: 'Search',
        //   icon: <Search className="icon" />,
        //   path: '/app/jobs/search'
        // },
        {
          id: 'resume-diagnosis',
          label: '简历诊断',
          icon: <FileText className="icon" />,
          path: '/app/resumes/diagnosis'
        },
        {
          id: 'interview-prep',
          label: '面试押题',
          icon: <Brain className="icon" />,
          path: '/app/interviews/prep'
        },
        {
          id: 'classic-question-bank',
          label: '经典题库',
          icon: <BookOpen className="icon" />,
          path: '/classic-questions'
        }
      ]
    }
  ];

  const handleNavItemClick = (item) => {
    // 如果点击的是经典题库，跳转到独立页面
    if (item.id === 'classic-question-bank') {
      window.open('/classic-questions', '_blank');
      return;
    }
    
    // 如果当前在简历诊断页面，且要跳转到其他页面，检查是否需要确认
    if (activeView === 'resume-diagnosis') {
      // 检查是否有确认函数（表示有未保存的结果）
      if (window.resumeDiagnosisConfirmLeave) {
        const canLeave = window.resumeDiagnosisConfirmLeave(() => {
          // 在确认后执行实际的导航逻辑
          setActiveView(item.id);
          navigate(item.path);
        });
        
        // 如果不能立即离开（需要用户确认），则直接返回
        if (!canLeave) {
          return;
        }
        // 如果能立即离开，继续执行导航
      }
    }
    
    // 如果当前在面试押题页面，且要跳转到其他页面，检查是否需要确认
    if (activeView === 'interview-prep') {
      // 检查是否有确认函数（表示有未保存的结果）
      if (window.interviewPrepConfirmLeave) {
        const canLeave = window.interviewPrepConfirmLeave(() => {
          // 在确认后执行实际的导航逻辑
          setActiveView(item.id);
          navigate(item.path);
        });
        
        // 如果不能立即离开（需要用户确认），则直接返回
        if (!canLeave) {
          return;
        }
        // 如果能立即离开，继续执行导航
      }
    }
    
    // 立即更新activeView状态
    setActiveView(item.id);
    // 然后导航到对应路径
    navigate(item.path);
  };

  // 处理点击职唛AI logo回到仪表盘
  const handleLogoClick = () => {
    // 如果当前在简历诊断页面，检查是否需要确认
    if (activeView === 'resume-diagnosis') {
      // 检查是否有确认函数（表示有未保存的结果）
      if (window.resumeDiagnosisConfirmLeave) {
        const canLeave = window.resumeDiagnosisConfirmLeave(() => {
          // 在确认后执行实际的导航逻辑
          setActiveView('dashboard');
          navigate('/app');
        });
        
        // 如果不能立即离开（需要用户确认），则直接返回
        if (!canLeave) {
          return;
        }
        // 如果能立即离开，继续执行导航
      }
    }
    
    // 如果当前在面试押题页面，检查是否需要确认
    if (activeView === 'interview-prep') {
      // 检查是否有确认函数（表示有未保存的结果）
      if (window.interviewPrepConfirmLeave) {
        const canLeave = window.interviewPrepConfirmLeave(() => {
          // 在确认后执行实际的导航逻辑
          setActiveView('dashboard');
          navigate('/app');
        });
        
        // 如果不能立即离开（需要用户确认），则直接返回
        if (!canLeave) {
          return;
        }
        // 如果能立即离开，继续执行导航
      }
    }
    
    setActiveView('dashboard');
    navigate('/app');
  };

  const renderContent = () => {
    if (!activeView || activeView === 'dashboard') {
      return <DashboardHome user={user} onNavigate={handleNavItemClick} sidebarCollapsed={sidebarCollapsed} />;
    }

    switch (activeView) {
      // case 'job-assistant':
      //   return (
      //     <ComponentWithSuspense>
      //       <JobSearchPage />
      //     </ComponentWithSuspense>
      //   );
      case 'resume-diagnosis':
        return (
          <ComponentWithSuspense>
            <ResumeDiagnosis />
          </ComponentWithSuspense>
        );
      case 'interview-prep':
        return (
          <ComponentWithSuspense>
            <InterviewPrep />
          </ComponentWithSuspense>
        );
      case 'mock-interview':
        return (
          <ComponentWithSuspense>
            <MockInterview />
          </ComponentWithSuspense>
        );
      case 'meeting-assistant':
        return (
          <ComponentWithSuspense>
            <MeetingAssistant onSessionModeChange={handleSessionModeChange} />
          </ComponentWithSuspense>
        );
      case 'setting':
        return <AccountSettingsPage />;
      case 'pricing':
        return <PricingPage />;
      case 'coop-promotion':
        return <CoopPromotionPage />;
      case 'ticket-support':
        return <TicketList />;
      default:
        return <DashboardHome user={user} onNavigate={handleNavItemClick} />;
    }
  };

  return (
    <DashboardContainer>
      {!sidebarHidden && (
      <Sidebar collapsed={sidebarCollapsed}>
        <ToggleButton onClick={toggleSidebar}>
          {sidebarCollapsed ? 
            <ChevronRight className="icon" /> : 
            <ChevronLeft className="icon" />
          }
        </ToggleButton>
        
        <SidebarHeader collapsed={sidebarCollapsed} onClick={handleLogoClick}>
          <h2>
            <img className="logo" src="/images/logos/logo.svg" alt="职唛 AI Logo" />
            职唛 AI
          </h2>
        </SidebarHeader>
        
        <SidebarNav>
          {navSections.map((section, sectionIndex) => (
            <NavSection 
              key={section.title || `section-${sectionIndex}`} 
              collapsed={sidebarCollapsed}
              hasTitle={!!section.title}
              isFirstWithoutTitle={!section.title && sectionIndex === 0}
            >
              {section.title && (
                <SectionTitle collapsed={sidebarCollapsed}>
                  {section.title}
                </SectionTitle>
              )}
              {section.items.map((item) => (
                <NavItem 
                  key={item.id} 
                  active={activeView === item.id}
                  collapsed={sidebarCollapsed}
                  data-tooltip={item.label}
                  onClick={() => handleNavItemClick(item)}
                  style={
                    item.id === 'meeting-assistant' 
                      ? { paddingBottom: '4px', marginBottom: '0px' } 
                      : item.id === 'dashboard' && !section.title
                      ? { 
                          paddingTop: '16px', 
                          paddingBottom: '16px', 
                          paddingLeft: '16px',
                          fontWeight: '600',
                          fontSize: '15px'
                        }
                      : undefined
                  }
                >
                  {item.icon && item.icon}
                  <span className="label">{item.label}</span>
                </NavItem>
              ))}
            </NavSection>
          ))}
        </SidebarNav>

        <SidebarFooter collapsed={sidebarCollapsed}>
          <UserProfile collapsed={sidebarCollapsed} className="user-dropdown">
            {(() => {
              const userInfo = formatUserInfo(user);
              return (
                <>
                  <div className="avatar" onClick={handleUserDropdownToggle}>
                    <UserAvatar user={user} size={24} />
                  </div>
                  <div className="info">
                    <div className="name">{userInfo.displayName}</div>
                    <div className="notification" onClick={handleNotificationClick}>
                      <Bell size={16} />
                      {newNotificationCount > 0 && (
                        <span className="badge">
                          {newNotificationCount > 99 ? '99+' : newNotificationCount}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
            
            {/* 用户下拉菜单（仅账户相关） */}
            {showUserDropdown && (
              <UserDropdownMenu collapsed={sidebarCollapsed}>
                <UserDropdownItem onClick={handleManageAccount}>
                  <Settings size={16} />
                  <span>账户设置</span>
                </UserDropdownItem>
                <UserDropdownItem onClick={handleSignOut}>
                  <LogOutIcon size={16} />
                  <span>退出</span>
                </UserDropdownItem>
              </UserDropdownMenu>
            )}
          </UserProfile>
          
          <CreditsSection collapsed={sidebarCollapsed}>
            <div className="credits-text">可用积分</div>
            <div className="credits-number">{balance}</div>
          </CreditsSection>
          
          <UpgradeCard collapsed={sidebarCollapsed}>
            {!vipInfoLoading ? (
              vipInfo.isVip ? (
                <button onClick={handleUpgradeClick}>
                  <Crown size={14} />
                  <span>VIP {vipInfo.vipLevel}</span>
                </button>
              ) : (
                <button onClick={handleUpgradeClick}>
                  {vipInfo.vipLevel === 'L0' && !vipInfo.isVip && (
                    <span className="trial-badge">体验版</span>
                  )}
                  <Crown size={14} />
                  <span>立即升级</span>
                </button>
              )
            ) : (
              <button style={{ opacity: 0.6, cursor: 'default' }}>
                <Crown size={14} />
                <span>加载中...</span>
              </button>
            )}
          </UpgradeCard>
          
          <FeedbackButton collapsed={sidebarCollapsed} onClick={handleCoopPromotionClick}>
            <Share2 size={14} />
            <span className="label">合作推广</span>
          </FeedbackButton>

          <FeedbackButton collapsed={sidebarCollapsed} onClick={handleFeedbackClick}>
            <Mail size={14} />
            <span className="label">意见反馈</span>
          </FeedbackButton>

          <FeedbackButton collapsed={sidebarCollapsed} onClick={handleTicketSupportClick}>
            <HelpCircle size={14} />
            <span className="label">工单支持</span>
          </FeedbackButton>
        </SidebarFooter>
      </Sidebar>
      )}

      <MainContent 
        sidebarCollapsed={sidebarCollapsed}
        sidebarHidden={sidebarHidden}
        nopadding={activeView === 'job-assistant' || sidebarHidden}
      >
        {renderContent()}
        <NotificationsPanel 
          isOpen={showNotifications} 
          onClose={() => setShowNotifications(false)}
          position={notificationPosition}
          onNewCountChange={setNewNotificationCount}
          prefetchedItems={prefetchedNotifications}
          maxHeight={Math.max(260, window.innerHeight - 100)}
        />
      </MainContent>

      {/* 意见反馈弹框 */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
      />
      
      {/* 用户设置弹框 */}
      <UserSettingsModal
        isOpen={showUserSettingsModal}
        onClose={handleUserSettingsClose}
        user={user}
        onSave={handleUserSettingsSave}
      />

      {/* 退出登录确认对话框 */}
      {showLogoutConfirm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '360px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              position: 'relative'
            }}
          >
            <button 
              onClick={() => setShowLogoutConfirm(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#a0aec0',
                cursor: 'pointer',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f7fafc';
                e.target.style.color = '#2d3748';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#a0aec0';
              }}
            >
              ×
            </button>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#2d3748',
              margin: '0 0 8px 0'
            }}>退出登录</h3>
            <p style={{
              fontSize: '14px',
              color: '#718096',
              margin: '0 0 20px 0'
            }}>确定要退出吗？</p>
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}>
              <button 
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  padding: '8px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  background: '#e2e8f0',
                  color: '#2d3748',
                  minWidth: '80px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#cbd5e0'}
                onMouseLeave={(e) => e.target.style.background = '#e2e8f0'}
              >
                取消
              </button>
              <button 
                onClick={confirmLogout}
                style={{
                  padding: '8px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
                  color: 'white',
                  minWidth: '80px',
                  boxShadow: '0 4px 12px rgba(229, 62, 62, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(229, 62, 62, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(229, 62, 62, 0.3)';
                }}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

    </DashboardContainer>
  );
};

export default DashboardPage;
